import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';

import { useProductCenterApi } from 'api';
import { repairKeys } from 'api/queries';
import { Id } from 'domain/Id';
import { CustomSeverityDialog } from 'library/components/controls/dialogs/CustomSeverityDialog';
import { UseDialogStateReturnType } from 'library/hooks/useDialogState';
import { useCheckConsoleDebuggingReadAccess } from 'main/auth/checkUserAccess';

export type ProductIndividualRepairDeleteDialogProps = {
	serialNumber: string;
} & Pick<UseDialogStateReturnType<Id>, 'data' | 'handleClose' | 'open'>;

export const ProductIndividualRepairDeleteDialog = ({
	data: dialogData,
	handleClose,
	open,
	serialNumber,
}: ProductIndividualRepairDeleteDialogProps) => {
	const { t } = useTranslation(['commonButton', 'productIndividual']);
	const { enqueueSnackbar } = useSnackbar();
	const { api } = useProductCenterApi();
	const { checkConsoleDebuggingReadAccess } =
		useCheckConsoleDebuggingReadAccess();
	const queryClient = useQueryClient();

	const repairMutation = useMutation<unknown, unknown, number>({
		mutationKey: repairKeys.getRepairsBySerialNumbers([serialNumber]),
		mutationFn: (id) => api.deleteRepair(id),
		onSuccess: async (_data, _) => {
			await queryClient.invalidateQueries({
				queryKey: repairKeys.all,
			});

			enqueueSnackbar(t('productIndividual:repairs-delete-success'), {
				variant: 'success',
			});

			handleClose();
		},
		onError: (error) => {
			if (checkConsoleDebuggingReadAccess()) {
				console.error(error);
			}

			enqueueSnackbar((error as Error)?.message, {
				variant: 'error',
				style: { whiteSpace: 'pre-line' },
			});
		},
	});

	const handleAccept = () => {
		if (dialogData) {
			const { id } = dialogData;
			void repairMutation.mutateAsync(id);
		}
	};

	return (
		<>
			{open && (
				<CustomSeverityDialog
					acceptButtonLabel={t('commonButton:yes')}
					declineButtonLabel={t('commonButton:no')}
					content={t('productIndividual:repairs-delete-question')}
					disabledAcceptButton={repairMutation.isPending}
					headerText={t('commonButton:delete')}
					isOpen
					onAccept={handleAccept}
					onDecline={handleClose}
				/>
			)}
		</>
	);
};
